import { Injectable, computed, inject, signal } from '@angular/core';
import { cancelableResource, debouncedSignal, QueryBuilder, QueryFilter } from '@softline/core';
import { SOFTLINE_FILTER_WWS_ABC_ANALYSIS } from '../abc-analysis.api';
import { AbcAnalysisOverviewRow } from '../types/abc-analysis-overview-row';
import { AbcAnalysisFilterStateService } from './abc-analysis-filter-state.service';
import { AbcAnalysisOverviewGridService } from './abc-analysis-overview-grid.service';

export const ABC_ANALYSIS_SEGMENT_ARTICLES_PAGE_SIZE = 25;

@Injectable()
export class AbcAnalysisSegmentArticlesService {
  private readonly overviewGridService = inject(AbcAnalysisOverviewGridService);
  private readonly filterState = inject(AbcAnalysisFilterStateService);
  private readonly entityFilterState = inject(SOFTLINE_FILTER_WWS_ABC_ANALYSIS);

  private readonly segment = signal('');
  private readonly searchInput = signal('');
  private readonly debouncedSearch = debouncedSignal(this.searchInput, 400);
  readonly page = signal(1);

  setSegment(segment: string): void {
    this.segment.set(segment);
    this.page.set(1);
  }

  setSearch(search: string): void {
    this.searchInput.set(search);
    this.page.set(1);
  }

  setPage(page: number): void {
    this.page.set(page);
  }

  private readonly articlesResource = cancelableResource({
    params: () => ({
      dateRangeFilter: this.filterState.dateRangeFilter(),
      filter: this.entityFilterState.queryFilter(),
      master: this.filterState.master(),
      segment: this.segment(),
      search: this.debouncedSearch(),
      page: this.page(),
    }),
    loader: async ({ params }) => {
      const startRow = (params.page - 1) * ABC_ANALYSIS_SEGMENT_ARTICLES_PAGE_SIZE;
      const q = new QueryBuilder<AbcAnalysisOverviewRow>()
        .start(startRow)
        .end(startRow + ABC_ANALYSIS_SEGMENT_ARTICLES_PAGE_SIZE)
        .column('product_number')
        .column('product_description')
        .column('segment')
        .column('net')
        .column('contribution_margin')
        .column('contribution_margin_percent')
        .column('sales_quantity')
        .column('inventory_quantity')
        .column('inventory_value')
        .filter(params.dateRangeFilter)
        // 'segment' isn't part of AbcAnalysisOverviewRow's output columns — same namespace
        // split as the other filters (see abc-analysis-overview-data.service.ts).
        .filter({ type: 'stringEquals', name: 'segment', value: params.segment } as unknown as QueryFilter<AbcAnalysisOverviewRow>);
      // See abc-analysis-overview-data.service.ts for why this cast is needed — filter
      // names live in a different namespace than AbcAnalysisOverviewRow's output columns.
      if (params.filter) q.filter(params.filter as QueryFilter<AbcAnalysisOverviewRow>);
      const search = params.search.trim();
      if (search) {
        q.filter({
          type: 'conditions',
          operator: 'OR',
          filters: [
            { type: 'stringContainsIgnoreCase', name: 'product_number', value: search },
            { type: 'stringContainsIgnoreCase', name: 'product_description', value: search },
          ],
        } as unknown as QueryFilter<AbcAnalysisOverviewRow>);
      }
      const query = q.build();
      const result = await this.overviewGridService.load({ query, master: params.master });
      // No group/agg columns requested, so the backend returns full per-article rows —
      // safe to treat result.data as AbcAnalysisOverviewRow[] rather than Partial<...>.
      return result.data as unknown as AbcAnalysisOverviewRow[];
    },
  });

  readonly articles = computed(() => this.articlesResource.value() ?? []);
  readonly isLoading = this.articlesResource.isLoading;
  readonly hasNextPage = computed(
    () => this.articles().length === ABC_ANALYSIS_SEGMENT_ARTICLES_PAGE_SIZE
  );
}
